import { useState, useEffect } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Search, 
  Plus,
  BookOpen,
  Building2,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Edit,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PayerRule, Payer } from '@/lib/authorization/types';
import { format, parseISO } from 'date-fns';
import { useAuthContext } from '@/contexts/AuthContext';

export default function RulesEngine() {
  const { user, profile } = useAuthContext();
  const [rules, setRules] = useState<PayerRule[]>([]);
  const [payers, setPayers] = useState<Payer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [payerFilter, setPayerFilter] = useState<string>('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PayerRule | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    payer_id: '',
    cpt_code: '',
    modifier: '',
    requires_prior_auth: true,
    required_documents: '',
    clinical_criteria: '',
    auth_validity_days: 90,
    special_instructions: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rulesResult, payersResult] = await Promise.all([
        supabase
          .from('payer_rules')
          .select('*, payer:payers(*)')
          .order('cpt_code', { ascending: true }),
        supabase
          .from('payers')
          .select('*')
          .order('name', { ascending: true })
      ]);

      if (rulesResult.error) throw rulesResult.error;
      if (payersResult.error) throw payersResult.error;

      setRules((rulesResult.data as unknown as PayerRule[]) || []);
      setPayers((payersResult.data as Payer[]) || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      payer_id: '',
      cpt_code: '',
      modifier: '',
      requires_prior_auth: true,
      required_documents: '',
      clinical_criteria: '',
      auth_validity_days: 90,
      special_instructions: '',
    });
    setEditingRule(null);
  };

  const handleSaveRule = async () => {
    if (!formData.payer_id || !formData.cpt_code) {
      toast.error('Payer and CPT code are required');
      return;
    }

    try {
      const ruleData = {
        payer_id: formData.payer_id,
        cpt_code: formData.cpt_code,
        modifier: formData.modifier || null,
        requires_prior_auth: formData.requires_prior_auth,
        required_documents: formData.required_documents 
          ? formData.required_documents.split(',').map(d => d.trim()) 
          : null,
        clinical_criteria: formData.clinical_criteria || null,
        auth_validity_days: formData.auth_validity_days,
        special_instructions: formData.special_instructions || null,
      };

      if (editingRule) {
        const { error } = await supabase
          .from('payer_rules')
          .update(ruleData)
          .eq('id', editingRule.id);
        if (error) throw error;
        toast.success('Rule updated');
      } else {
        const { error } = await supabase
          .from('payer_rules')
          .insert(ruleData);
        if (error) throw error;
        toast.success('Rule created');
      }

      setAddDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving rule:', error);
      toast.error('Failed to save rule');
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;

    try {
      const { error } = await supabase
        .from('payer_rules')
        .delete()
        .eq('id', ruleId);
      if (error) throw error;
      toast.success('Rule deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete rule');
    }
  };

  const openEditDialog = (rule: PayerRule) => {
    setEditingRule(rule);
    setFormData({
      payer_id: rule.payer_id,
      cpt_code: rule.cpt_code,
      modifier: rule.modifier || '',
      requires_prior_auth: rule.requires_prior_auth,
      required_documents: rule.required_documents?.join(', ') || '',
      clinical_criteria: rule.clinical_criteria || '',
      auth_validity_days: rule.auth_validity_days,
      special_instructions: rule.special_instructions || '',
    });
    setAddDialogOpen(true);
  };

  // Filter rules
  const filtered = rules.filter(rule => {
    const matchesSearch = 
      rule.cpt_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.payer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.clinical_criteria?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPayer = payerFilter === 'all' || rule.payer_id === payerFilter;
    
    return matchesSearch && matchesPayer;
  });

  // Group rules by payer for summary
  const payerRuleCounts = rules.reduce((acc, rule) => {
    const payerName = rule.payer?.name || 'Unknown';
    acc[payerName] = (acc[payerName] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  if (loading) {
    return (
      <OpsLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse text-muted-foreground">Loading rules...</div>
        </div>
      </OpsLayout>
    );
  }

  return (
    <OpsLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Rules Engine</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Payer-specific authorization requirements by CPT code
            </p>
          </div>
          <Dialog open={addDialogOpen} onOpenChange={(open) => {
            setAddDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add Rule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingRule ? 'Edit Rule' : 'Add New Rule'}</DialogTitle>
                <DialogDescription>
                  Define authorization requirements for a specific payer and CPT code
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Payer *</Label>
                    <Select 
                      value={formData.payer_id} 
                      onValueChange={(v) => setFormData({...formData, payer_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select payer" />
                      </SelectTrigger>
                      <SelectContent>
                        {payers.map(payer => (
                          <SelectItem key={payer.id} value={payer.id}>
                            {payer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>CPT Code *</Label>
                    <Input
                      value={formData.cpt_code}
                      onChange={(e) => setFormData({...formData, cpt_code: e.target.value})}
                      placeholder="27447"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Modifier</Label>
                    <Input
                      value={formData.modifier}
                      onChange={(e) => setFormData({...formData, modifier: e.target.value})}
                      placeholder="59"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Auth Validity (Days)</Label>
                    <Input
                      type="number"
                      value={formData.auth_validity_days}
                      onChange={(e) => setFormData({...formData, auth_validity_days: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Requires Prior Auth?</Label>
                  <Select 
                    value={formData.requires_prior_auth ? 'yes' : 'no'}
                    onValueChange={(v) => setFormData({...formData, requires_prior_auth: v === 'yes'})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Required Documents (comma-separated)</Label>
                  <Input
                    value={formData.required_documents}
                    onChange={(e) => setFormData({...formData, required_documents: e.target.value})}
                    placeholder="Operative notes, X-rays, Clinical notes"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Clinical Criteria</Label>
                  <Textarea
                    value={formData.clinical_criteria}
                    onChange={(e) => setFormData({...formData, clinical_criteria: e.target.value})}
                    placeholder="Medical necessity criteria..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Special Instructions</Label>
                  <Textarea
                    value={formData.special_instructions}
                    onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
                    placeholder="Any special submission requirements..."
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => {
                  setAddDialogOpen(false);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button onClick={handleSaveRule}>
                  {editingRule ? 'Update Rule' : 'Create Rule'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <BookOpen className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{rules.length}</p>
                  <p className="text-xs text-muted-foreground">Total Rules</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Building2 className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{Object.keys(payerRuleCounts).length}</p>
                  <p className="text-xs text-muted-foreground">Payers Covered</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {rules.filter(r => r.requires_prior_auth).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Auth Required</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {rules.filter(r => !r.requires_prior_auth).length}
                  </p>
                  <p className="text-xs text-muted-foreground">No Auth Needed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by CPT code, payer, or criteria..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={payerFilter} onValueChange={setPayerFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by payer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payers</SelectItem>
              {payers.map(payer => (
                <SelectItem key={payer.id} value={payer.id}>
                  {payer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Rules Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payer</TableHead>
                    <TableHead>CPT Code</TableHead>
                    <TableHead>Modifier</TableHead>
                    <TableHead>Auth Required</TableHead>
                    <TableHead>Validity</TableHead>
                    <TableHead>Required Docs</TableHead>
                    <TableHead className="w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No rules found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{rule.payer?.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {rule.cpt_code}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {rule.modifier ? (
                            <Badge variant="secondary" className="font-mono">
                              {rule.modifier}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {rule.requires_prior_auth ? (
                            <Badge className="bg-amber-500/10 text-amber-500">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Required
                            </Badge>
                          ) : (
                            <Badge className="bg-primary/10 text-primary">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Not Required
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            {rule.auth_validity_days} days
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {rule.required_documents?.slice(0, 2).map((doc, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {doc}
                              </Badge>
                            ))}
                            {rule.required_documents && rule.required_documents.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{rule.required_documents.length - 2}
                              </Badge>
                            )}
                            {!rule.required_documents?.length && (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => openEditDialog(rule)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleDeleteRule(rule.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Empty State for No Payers */}
        {payers.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="p-6 text-center">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold text-lg">No Payers Configured</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Add payers first before creating authorization rules
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </OpsLayout>
  );
}
